import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../service/auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  private navigateToLogin(): void {
    if (this.authService.isFederation()) {
      this.router.navigate(['/federation/auth/login']);
      return;
    }
    if (this.authService.isClub()) {
      this.router.navigate(['/club/auth/login']);
      return;
    }
    this.router.navigate(['/']);
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('auth/login') ||
           url.includes('auth/logout') ||
           url.includes('auth/refresh');
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const clonedRequest = request.clone({
      withCredentials: true,
      setHeaders: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(request.url)) {
          return this.handleTokenRefresh(clonedRequest, next, error);
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => next.handle(request))
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(false);

    return this.authService.refreshToken().pipe(
      switchMap(() => {
        this.isRefreshing = false;
        this.refreshDone$.next(true);
        return next.handle(request);
      }),
      catchError(() => {
        this.isRefreshing = false;
        this.authService.clearLocalAuth();
        this.navigateToLogin();
        return throwError(() => originalError);
      })
    );
  }
}
